import React, { useEffect, useRef, useState } from "react";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useAuth } from "../../context/AuthContext";
import { PaperPlaneRight, Sparkle, ShieldCheck } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const SUGGESTIONS = ["What is happening on this symbol?", "Why is R.I.O bullish or bearish right now?", "Show me the strongest scanner setups.", "Review my recent demo trades.", "Explain the latest CISD."];

// Claude R.I.O — read-only support agent (server-side tools; streaming SSE). Cannot execute trades.
export default function AiPanel() {
  const ws = useWorkspace();
  useAuth();
  const [sessionId] = useState(() => `rio-${Date.now().toString(36)}`);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (text) => {
    const q = (text ?? input).trim(); if (!q || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "ai", text: "", tools: [] }]);
    try {
      const res = await fetch(`${API}/api/ai/chat`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("felacoo_token")}` },
        body: JSON.stringify({ session_id: sessionId, message: q, symbol: ws.symbol, timeframe: ws.tf.id }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d?.detail?.message || d?.detail || `HTTP ${res.status}`); }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          setMsgs((m) => { const last = { ...m[m.length - 1] };
            if (ev.type === "delta") last.text += ev.content; else if (ev.type === "tool") last.tools = [...(last.tools || []), ev.name]; else if (ev.type === "error") last.text += `\n⚠ ${ev.message}`;
            return [...m.slice(0, -1), last]; });
        }
      }
    } catch (e) { setMsgs((m) => [...m.slice(0, -1), { role: "ai", text: `⚠ ${e.message}` }]); }
    setBusy(false);
  };

  return (
    <div className="flex flex-col h-full" data-testid="ai-panel">
      <div className="px-3 py-2 border-b border-border text-[10px] text-muted-foreground flex items-center gap-1.5"><ShieldCheck size={12} className="text-buy" /> Read-only · cannot place trades · context {ws.symbol} {ws.tf.id}</div>
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin p-3 space-y-3">
        {msgs.length === 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><Sparkle size={16} weight="fill" className="text-primary" /> Claude R.I.O</div>
            <p className="text-xs text-muted-foreground">Ask about structure, CISD, confluence, scanner setups, alerts, blocked trades or your journal. Answers are grounded in live Fela R.I.O data.</p>
            {SUGGESTIONS.map((s) => <button key={s} data-testid="ai-suggestion" onClick={() => send(s)} className="block w-full text-left px-2.5 py-1.5 rounded-md border border-border text-xs hover:bg-secondary">{s}</button>)}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} data-testid={`ai-msg-${m.role}`} className={`text-xs whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "ml-6 px-3 py-2 rounded-lg bg-primary/15 text-foreground" : "mr-2"}`}>
            {m.role === "ai" && m.tools?.length > 0 && <div className="text-[10px] text-muted-foreground mb-1">↳ {m.tools.join(", ")}</div>}
            {m.text || (busy && i === msgs.length - 1 ? <span className="text-muted-foreground animate-pulse">thinking…</span> : "")}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-2 border-t border-border flex gap-1.5">
        <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Claude R.I.O…" className="flex-1 h-9 px-3 rounded-md border border-border bg-background text-xs outline-none focus:border-primary" />
        <button data-testid="ai-send" type="submit" disabled={busy || !input.trim()} className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40"><PaperPlaneRight size={14} weight="fill" /></button>
      </form>
    </div>
  );
}
